/**
 * ERP / Functional Area Consolidation — screen resolution.
 *
 * Precedent: the pattern every product in this repository uses —
 * `apps/portal/web/src/pages/portfolio_definition/PortfolioDefinitionModule.tsx` and
 * `pages/apr/AprModule.tsx:45-120`. The pattern is cloned, not imported; nothing under
 * `pages/apr/` is touched (D-003), and `portfolio_definition` is a different product again, so its
 * file is read rather than reused (`CLAUDE.md` §1.7).
 *
 * THE RULE THIS FILE OBEYS, which is `AprModule.tsx:45-49` in its own words: screens are keyed by
 * the **section-path trail**, so a route is derived from the catalogue rather than written twice —
 * **"a bucket that nests deeper needs a catalogue entry and a screen, never a new route"** — and
 * **a module must not draw a tab strip**, because that would be a second navigation model
 * competing with the shell's. A key with no entry here falls through to `ModulePlaceholder`, which
 * is how a nav row for a screen still being built behaves: visible, navigable, and honest about
 * being empty rather than a dead link or a crash.
 *
 * ── WHAT THE LEAD HAS TO ADD TO `packages/entitlements/src/catalog.ts` ──────────────────
 *
 * The module's entry is there with `sections: NO_SECTIONS` (`catalog.ts:648-658`), and
 * `catalog.ts` is the lead's file — **not edited here**. Until it has sections, this module has no
 * drawer rows and `/erp_consolidation` resolves to nothing, so these screens are reachable only by
 * typing their URLs. The six sections now needed, in this order:
 *
 *     sections('erp_consolidation', ['Initiatives', 'Business Units', 'Capability Model',
 *                                   'Assessment Grid', 'Scores', 'Scoring Definition'])
 *
 * which derives the keys and paths this file is written against:
 *
 *     initiatives         → /erp_consolidation/initiatives
 *     business-units      → /erp_consolidation/business-units
 *     capability-model    → /erp_consolidation/capability-model
 *     assessment-grid     → /erp_consolidation/assessment-grid
 *     scores              → /erp_consolidation/scores
 *     scoring-definition  → /erp_consolidation/scoring-definition
 *
 * **ONE MORE ROW IS NEEDED NOW AND IS NOT ADDED HERE** — `catalog.ts` is the lead's file. The
 * capability template library is built and routed as `capability-templates`, so the catalogue's
 * section list needs:
 *
 *     'Capability Templates' → /erp_consolidation/capability-templates   icon: 'schema'
 *
 * placed immediately AFTER 'Capability Model', because that is where a user looks for it: the
 * templates are what the New Capability Model panel's "Start from a template" picker offers, and the
 * two screens are read together. `schema` is already registered in `packages/ui-kit/src/icons.tsx`,
 * so it resolves; an unregistered name renders no icon at all, silently, which that file records
 * having been caught four times.
 *
 * The ORDER is the order the work is done in: a model is weighted, then assessed, then read. The
 * scoring definition sits last because it is the administrator's screen rather than part of the
 * sequence, and because `ScoringModel.tsx` — the screen it clones — lives under Settings in APR.
 * There is no row for the calculation breakdown: it opens from a row on Scores and on the grid,
 * which is what its precedent does.
 *
 * `keyOf` (`catalog.ts:33-38`) lower-cases and hyphenates a label, so those keys follow from those
 * labels and nothing here is hand-written. The module's `status` can also drop from `'planned'`
 * once a screen lands — that chip says "Coming soon", and it stops being honest.
 *
 * ── THE TWO SUB-PATHS, AND WHY THEY ARE NOT SECTIONS ───────────────────────────────────
 *
 * `/erp_consolidation/initiatives/<id>/setup` is a record BENEATH a screen, not a catalogue
 * section: `resolveNavigation` stops at the first segment that does not name a child
 * (`navigation.ts:66-79`) and hands back the rest, which is the same mechanism
 * `portfolio_definition` uses for `opportunity-registry/<id>/edit` and SPN for a discovery-session
 * code. It needs no catalogue entry and no new route.
 *
 * The capability model's own selection is a QUERY STRING — `?initiative=…&model=…` — which is
 * `FrameworkBuilder.tsx:933-949`'s pattern and its reasoning: the URL is the single source of truth
 * for which model is open, so the load effect that keys on it does the reload and a shared link
 * opens the same model.
 */
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { resolveNavigation } from '@platform/entitlements';
import type { PortalModule } from '../../api';
import { ModulePlaceholder } from '../ModulePlaceholder';
import { SectionIndex } from '../SectionIndex';
import { InitiativesList } from './initiatives/InitiativesList';
import { InitiativeSetup } from './initiatives/InitiativeSetup';
import { BusinessUnitHierarchy } from './businessUnits/BusinessUnitHierarchy';
import { CapabilityModelEditor } from './capability/CapabilityModelEditor';
import { CapabilityTemplateLibrary } from './capability/CapabilityTemplateLibrary';
import { AssessmentGrid } from './assessment/AssessmentGrid';
import { SystemSelection } from './systems/SystemSelection';
import { ScoresList } from './scores/ScoresList';
import { ScoringDefinition } from './scoring/ScoringDefinition';
import { KnowledgeLibrary } from './knowledge/KnowledgeLibrary';

const SCREENS: Record<string, () => JSX.Element> = {
  /* SCREEN_MAP #1, #2 — INI-01-03. The detail is a right-side panel this screen owns, the way
     APR Model owns its own panel, so it is one entry rather than two. */
  initiatives: () => <InitiativesList />,
  /* SCREEN_MAP #4 — BU-01-02. UI_GAPS §4 Option B. */
  'business-units': () => <BusinessUnitHierarchy />,
  /* SCREEN_MAP #5 — CAP-01-06, and §6.1's 100% rule. */
  'capability-model': () => <CapabilityModelEditor />,
  /* SCREEN_MAP #25 — the template library, §6.1-§6.7. The reusable structures a capability model
     can be created FROM; `?template=<id>` opens one, the same way `?model=` opens a model. */
  'capability-templates': () => <CapabilityTemplateLibrary />,
  /* SOL-01 + `0107` — ASSESSMENT RUNS. A run is a named, versioned grid: one business unit's
     systems, assessed for one functional area of one capability model. Creating runs and
     filling them with systems happens here; recording the percentages happens on the grid. */
  'assessment-runs': () => <SystemSelection />,
  /* SCREEN_MAP #8 — ASM-01-06. One functional area at a time, which is D-009's own answer to
     the scale question and what §7b.4 requires. */
  'assessment-grid': () => <AssessmentGrid />,
  /* SCO-02 — every unit scored, quadranted and ranked. The calculation breakdown (SCO-03) opens
     from a row here and from a row on the grid, which is what its precedent does: a modal that
     explains the row you are looking at, so closing it puts you back on that row. */
  scores: () => <ScoresList />,
  /* SCREEN_MAP #24 — SCO-01, and D-015's warning is the point of it. */
  'scoring-definition': () => <ScoringDefinition />,
  /* D-025 — APR's Knowledge Library · Architecture & Process Diagrams, reached from this module:
     one shared library, so a document added or deleted on either screen appears on both. */
  'knowledge-library': () => <KnowledgeLibrary />,
};

export interface ErpConsolidationModuleProps {
  modules: PortalModule[];
}

export function ErpConsolidationModule({ modules }: ErpConsolidationModuleProps) {
  const module = modules.find((m) => m.id === 'erp_consolidation');
  /*
   * No sections yet means no navigable module. Every other product does the same thing here, and
   * it is what keeps an unlicensed or not-yet-registered module from rendering a half-shell:
   * `PortfolioDefinitionModule.tsx` returns the same redirect on the same condition.
   */
  if (!module || module.sections.length === 0) return <Navigate to="/" replace />;
  return <Content modules={modules} module={module} />;
}

function Content({ modules, module }: { modules: PortalModule[]; module: PortalModule }) {
  const location = useLocation();
  const navigate = useNavigate();

  const at = resolveNavigation(location.pathname, [module]);

  /*
   * `/erp_consolidation` itself LANDS ON INITIATIVES — the owner's instruction, 2026-10-05: "at
   * this stage i should see the screen Initiative and not 'start'".
   *
   * The other products each land on their own Start Menu here, cloned from their source app.
   * This module has no source app and no start screen was named for it, so it landed on the
   * platform's plain section index instead — a signpost listing six rows that are already
   * listed in the drawer beside it. An extra click to reach the only screen a user can begin
   * from is not a landing page.
   *
   * IT NAMES INITIATIVES, and no longer takes whatever happens to be first.
   *
   * It used to redirect to `sections[0]`, on the reasoning that the landing should follow the
   * nav order rather than go stale. That broke on 2026-10-07, when the owner asked for
   * *"Business Unit menu should be positioned above 'initiative' menu"*: re-ordering the
   * catalogue would have silently moved the landing screen to Business Units, which is a change
   * he did not ask for and which contradicts the instruction above that is still standing.
   *
   * So the landing is pinned to the Initiatives section BY KEY, and falls back to the first
   * section only if that key ever stops existing — which keeps the "never point somewhere
   * stale" property without tying the landing to a running order that is the owner's to change.
   */
  if (at.trail.length === 0) {
    const landing =
      module.sections.find((section) => section.key === 'initiatives') ?? module.sections[0];
    if (landing) return <Navigate to={landing.path} replace />;
    return (
      <SectionIndex
        title={module.name}
        description={module.description}
        sections={module.sections}
        onOpen={(path) => navigate(path)}
      />
    );
  }

  const deepest = at.trail[at.trail.length - 1];

  if (!at.section || !deepest) {
    return (
      <SectionIndex
        title={deepest ? deepest.label : module.name}
        sections={deepest?.sections ?? []}
        onOpen={(path) => navigate(path)}
      />
    );
  }

  const key = at.trail.map((s) => s.key).join('/');

  /*
   * `/…/initiatives/<id>/setup` — the readiness checklist for one initiative (UI_GAPS decision
   * #7). A record beneath a screen, not a section; the id is read off the path exactly as
   * `portfolio_definition` reads an opportunity id.
   */
  if (key === 'initiatives') {
    const rest = location.pathname
      .slice(at.section.path.length + 1)
      .split('/')
      .filter(Boolean);
    if (rest[0] && rest[1] === 'setup') {
      return <InitiativeSetup initiativeId={decodeURIComponent(rest[0])} />;
    }
  }

  const render = SCREENS[key];
  if (!render) {
    return (
      <ModulePlaceholder
        modules={modules}
        module={module}
        section={{ key: at.section.key, label: at.section.label }}
      />
    );
  }
  return render();
}
